import { icons } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import React from "react"

/**
 * Aliases for icons renamed in lucide-react v0.300+.
 * Maps old PascalCase name → new PascalCase name.
 */
const ALIASES: Record<string, string> = {
  Home: "House",
  BarChart: "ChartBar",
  BarChart3: "ChartBarStacked",
  AreaChart: "ChartArea",
  Filter: "ListFilter",
  CheckSquare: "SquareCheck",
  PlayCircle: "CirclePlay",
  Code2: "CodeXml",
  PlusCircle: "CirclePlus",
  MoreHorizontal: "Ellipsis",
  CheckCircle: "CircleCheck",
  Columns: "Columns2",
  HelpCircle: "CircleQuestionMark",
}

/**
 * Brand icons removed from lucide-react.
 * Inline SVG components as fallbacks.
 */
const BRAND_ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  Chrome: (props) =>
    React.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      width: 24,
      height: 24,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      ...props,
    },
      React.createElement("circle", { cx: 12, cy: 12, r: 10 }),
      React.createElement("circle", { cx: 12, cy: 12, r: 4 }),
      React.createElement("line", { x1: 21.17, y1: 8, x2: 12, y2: 8 }),
      React.createElement("line", { x1: 3.95, y1: 6.06, x2: 8.54, y2: 14 }),
      React.createElement("line", { x1: 10.88, y1: 21.94, x2: 15.46, y2: 14 }),
    ),
}

/**
 * Resolve a kebab-case icon name (e.g. "shopping-cart") to the
 * corresponding lucide-react component (ShoppingCart).
 *
 * Handles renamed icons via alias map and brand icons via SVG fallback.
 * Returns undefined if the icon is not found.
 */
export function resolveIcon(name: string): LucideIcon | React.FC<React.SVGProps<SVGSVGElement>> | undefined {
  if (!name) return undefined

  // Convert kebab-case to PascalCase: "shopping-cart" → "ShoppingCart"
  const pascal = name
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("")

  const lookup = icons as Record<string, LucideIcon>
  return lookup[pascal] ?? lookup[ALIASES[pascal] ?? ""] ?? BRAND_ICONS[pascal]
}
